import React, { memo, useState, useEffect, KeyboardEvent, ChangeEvent } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { IVin } from 'types/carrier'
import {
  saveCarrierVins,
  addCarrierVin,
  deleteCarrierVin,
  closeVinsDialog,
} from 'apps/carrier/utils/actions/carrierVins'
import { IStore } from 'apps/carrier/utils/reducers'
import { TextInput } from 'components/atoms'
import { FormModal } from 'transfix-ui'
import { handleKeyboardEvent } from 'utils/handleKeyboardEvent'
import { getVinErrors } from 'utils/vins'
import { KEY_VALUES } from 'constants/keyValues'
import { VALID_CRITERIA, VIN_ERROR_MESSAGES } from 'constants/vins'
import { VinGrid } from '../VinGrid'

interface ISelector {
  isOpen: boolean
  error?: string
  saving: boolean
}

interface IEditVinsProps {
  carrierId: string
  vins: IVin[]
}

export const EditVinsDialog = memo<IEditVinsProps>(({ carrierId, vins }) => {
  const { error, isOpen, saving } = useSelector<IStore, ISelector>(({ carrierVins }) => ({
    isOpen: carrierVins.showDialog,
    error: carrierVins.error,
    saving: carrierVins.saving,
  }))
  const [inputVin, setInputVin] = useState('')
  const [validationError, setValidationError] = useState('')
  useEffect(() => {
    if (isOpen) {
      setInputVin('')
      setValidationError('')
    }
  }, [isOpen])

  const inputChangeHandler = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setInputVin(value)
    if (!value) setValidationError('')
  }

  const dispatch = useDispatch()
  const onModalResult = (action?: string) => {
    dispatch(action === 'confirm' ? saveCarrierVins(carrierId, vins) : closeVinsDialog())
  }

  const deleteVin = (vin: IVin) => dispatch(deleteCarrierVin(vin))
  const handleKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    const vin = handleKeyboardEvent(e, KEY_VALUES.ENTER)
    if (vin) {
      const errors = getVinErrors(
        vin,
        vins.filter(({ _destroy }) => !_destroy),
        VALID_CRITERIA,
        VIN_ERROR_MESSAGES
      )
      if (errors) {
        setValidationError(errors.join(' '))
      } else {
        setInputVin('')
        dispatch(addCarrierVin(vin))
      }
    }
  }

  return (
    <FormModal
      size={8}
      title='Edit Scheduled VINs'
      open={isOpen}
      onClose={onModalResult}
      error={error}
      actions={[
        {
          label: 'Cancel',
          type: 'dismiss',
          dataTest: 'vins-cancel',
          disabled: saving,
        },
        {
          label: 'Save',
          type: 'confirm',
          dataTest: 'vins-save',
          disabled: Boolean(inputVin) || saving,
        },
      ]}
      dataTest='edit-vins-dialog'>
      <div className='row'>
        <div className='col-xs-6'>
          <TextInput
            dataTest='vin-input'
            id='vinInput'
            placeholder='1234'
            label='VIN'
            value={inputVin || ''}
            onChange={inputChangeHandler}
            onKeyUp={handleKeyUp}
            invalid={Boolean(validationError)}
            validationMessage={validationError}
          />
        </div>
      </div>
      <div className='row'>
        <div className='col-md-6'>
          <VinGrid vins={vins} deleteVin={deleteVin} />
        </div>
      </div>
    </FormModal>
  )
})
